import {
  buildArchive,
  mergeArchive,
  recordBackupTaken,
  type RestoreSummary,
} from '@/features/backup';
import { db, isPortableSetting, settings } from '@/lib/db';
import { defaultDeviceName } from '@/lib/platform';

import { isSyncFile, type StoredFolder, type SyncFolder, type SyncFolderFile } from './folder';

/**
 * Sync through a folder someone else already syncs.
 *
 * The app has no server and is not getting one. What most pilots do have is
 * iCloud Drive, OneDrive, Dropbox or Google Drive, each of which already
 * moves files between their devices. So this writes one file per device into
 * a folder the pilot picks, and reads everyone else's:
 *
 * ```
 * Flight Data Viewer/                     ← in iCloud Drive, OneDrive, …
 *   Flight Data Viewer - Mac (3f9a2c1b).fdv
 *   Flight Data Viewer - iPhone (77d0e4aa).fdv
 * ```
 *
 * **One writer per file** is the whole trick. Cloud drives do not lock; two
 * devices writing one file produce "conflicted copy" duplicates and lose one
 * side. A device here only ever writes its own file, and only ever reads the
 * others, so no file has two writers and the drive never has a conflict to
 * resolve. Every file is a complete `.fdv` archive — each one is also a
 * backup, restorable on its own — and merging one is `mergeArchive` in
 * `'sync'` mode: newest edit wins, deletions travel as tombstones.
 *
 * A file is merged only when its size or modified time has changed since the
 * last merge, and this device's file is rewritten only when the data behind it
 * has changed, so an idle app does no work. See `docs/SYNC.md`.
 */

export const SYNC_KEYS = {
  folder: 'sync.folder',
  deviceId: 'sync.deviceId',
  deviceName: 'sync.deviceName',
  includeOriginals: 'sync.includeOriginals',
  /** `{ [filename]: 'mtime:size' }` — the version of each other device's file last merged. */
  seen: 'sync.seen',
  /** `dataFingerprint()` when this device's file was last written. */
  lastWritten: 'sync.lastWritten',
  lastSyncAt: 'sync.lastSyncAt',
} as const;

/**
 * Where a device's file goes. A device can sync to both at once, which is
 * what joins a desktop app writing a folder on disk to an iPhone that can
 * only reach iCloud through CloudKit (`icloud.ts`).
 */
export type SyncTarget = 'folder' | 'icloud';

/**
 * Bookkeeping per target. The folder keeps the key names it had before there
 * were two targets, so an existing install does not re-read every file.
 */
function targetKeys(target: SyncTarget): { seen: string; lastWritten: string } {
  return target === 'folder'
    ? { seen: SYNC_KEYS.seen, lastWritten: SYNC_KEYS.lastWritten }
    : { seen: `${SYNC_KEYS.seen}.${target}`, lastWritten: `${SYNC_KEYS.lastWritten}.${target}` };
}

/** Forget what was read from and written to a target, so the next sync starts over there. */
export async function resetTarget(target: SyncTarget): Promise<void> {
  const keys = targetKeys(target);
  await settings.set(keys.seen, {});
  await settings.set(keys.lastWritten, null);
}

export interface SyncIdentity {
  id: string;
  name: string;
}

/** This device's id and name, creating the id on first use. */
export async function syncIdentity(): Promise<SyncIdentity> {
  let id = await settings.get<string | null>(SYNC_KEYS.deviceId, null);
  if (id === null) {
    id = crypto.randomUUID();
    await settings.set(SYNC_KEYS.deviceId, id);
  }
  const name = await settings.get<string | null>(SYNC_KEYS.deviceName, null);
  return { id, name: name === null || name.trim() === '' ? defaultDeviceName() : name.trim() };
}

/**
 * The name of a device's file. The id's first eight characters keep two
 * devices both called "Mac" apart; the name makes the folder readable.
 */
export function deviceFileName(identity: SyncIdentity): string {
  const safe = identity.name.replace(/[\\/:*?"<>|]/g, '').trim() || 'Device';
  return `Flight Data Viewer - ${safe} (${identity.id.slice(0, 8)}).fdv`;
}

/** Whether a filename is this device's, whatever name it was written under. */
export function isOwnFile(name: string, identity: SyncIdentity): boolean {
  return name.endsWith(`(${identity.id.slice(0, 8)}).fdv`);
}

/**
 * A cheap summary that changes whenever anything a sync file carries changes.
 *
 * Counts catch additions and deletions; the newest `updatedAt` catches edits.
 * Track points are immutable and arrive with their track, so the track count
 * covers them. Device-local settings are bookkeeping written by sync itself
 * and are left out, or every sync would look like a change.
 */
export async function dataFingerprint(): Promise<string> {
  const newest = (rows: readonly { updatedAt: string }[]) =>
    rows.reduce((max, row) => (row.updatedAt > max ? row.updatedAt : max), '');
  const [tracks, trackFiles, flights, aircraft, entries, checks, settingRows, tombstones] =
    await Promise.all([
      db.tracks.count(),
      db.trackFiles.count(),
      db.flights.toArray(),
      db.aircraft.toArray(),
      db.logbookEntries.toArray(),
      db.logbookChecks.toArray(),
      db.settings.toArray(),
      db.tombstones.toArray(),
    ]);
  const portable = settingRows.filter((row) => isPortableSetting(row.key));
  return [
    tracks,
    trackFiles,
    flights.length,
    newest(flights),
    aircraft.length,
    newest(aircraft),
    entries.length,
    newest(entries),
    checks.length,
    newest(checks),
    portable.length,
    newest(portable),
    tombstones.length,
    tombstones.reduce((max, row) => (row.deletedAt > max ? row.deletedAt : max), ''),
  ].join('|');
}

export interface SyncResult {
  /** Other devices' files merged this time: only those that changed. */
  merged: { name: string; summary: RestoreSummary }[];
  /** Files that could not be read or merged, with why. */
  failed: { name: string; message: string }[];
  /** Whether this device's own file was rewritten. */
  wrote: boolean;
  /** Every sync file in the folder, this device's included. */
  files: SyncFolderFile[];
  at: string;
}

function versionOf(file: SyncFolderFile): string {
  return `${String(file.lastModified)}:${String(file.size)}`;
}

/**
 * One round: merge what changed, then write this device's file if anything
 * here has changed since it was last written.
 */
export async function runSync(
  folder: SyncFolder,
  target: SyncTarget = 'folder',
): Promise<SyncResult> {
  const keys = targetKeys(target);
  const identity = await syncIdentity();
  const seen = { ...(await settings.get<Record<string, string>>(keys.seen, {})) };
  const files = await folder.list();

  const merged: SyncResult['merged'] = [];
  const failed: SyncResult['failed'] = [];
  for (const file of files) {
    if (isOwnFile(file.name, identity) || seen[file.name] === versionOf(file)) {
      continue;
    }
    try {
      const summary = await mergeArchive(await folder.read(file.name), { mode: 'sync' });
      merged.push({ name: file.name, summary });
      seen[file.name] = versionOf(file);
    } catch (error) {
      failed.push({
        name: file.name,
        message: error instanceof Error ? error.message : 'Could not be read.',
      });
    }
  }
  // Forget files no longer in the folder, so the map does not grow forever.
  const present = new Set(files.map((file) => file.name));
  await settings.set(
    keys.seen,
    Object.fromEntries(Object.entries(seen).filter(([name]) => present.has(name))),
  );

  const ownName = deviceFileName(identity);
  const fingerprint = await dataFingerprint();
  const lastWritten = await settings.get<string | null>(keys.lastWritten, null);
  const ownPresent = files.some((file) => file.name === ownName);
  let wrote = false;
  if (fingerprint !== lastWritten || !ownPresent) {
    const bytes = await buildArchive({
      includeOriginals: await settings.get(SYNC_KEYS.includeOriginals, false),
      device: identity,
    });
    await folder.write(ownName, bytes);
    await settings.set(keys.lastWritten, fingerprint);
    // A complete archive now sits outside the browser, which is everything
    // the backup reminder asks for.
    await recordBackupTaken();
    wrote = true;
  }

  // Renaming this device renames its file. The old one would otherwise sit
  // in the folder for ever, a stale copy under a name nobody uses.
  for (const file of files) {
    if (file.name !== ownName && isOwnFile(file.name, identity)) {
      await folder.remove(file.name).catch(() => undefined);
    }
  }

  const at = new Date().toISOString();
  await settings.set(SYNC_KEYS.lastSyncAt, at);
  const listed = await folder.list();
  return { merged, failed, wrote, files: listed.filter((file) => isSyncFile(file.name)), at };
}

export async function readStoredFolder(): Promise<StoredFolder | null> {
  const stored = await settings.get<StoredFolder | null>(SYNC_KEYS.folder, null);
  if (stored === null || typeof stored !== 'object') {
    return null;
  }
  return stored;
}

export async function storeFolder(folder: StoredFolder | null): Promise<void> {
  await settings.set(SYNC_KEYS.folder, folder);
  // A new folder holds different files; what was seen in the old one says
  // nothing about them, and this device's file is not in it yet.
  await resetTarget('folder');
}
